import { useCallback, useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  Calendar,
  Calculator,
  TrendingUp,
  DollarSign,
  CreditCard,
  Layers2,
} from "lucide-react";
import { caixaGeralService } from "../../services/apiService";
import { formatarData } from "../../utils/FormatarData";
import { formatCurrencyMoeda } from "../../utils/FormatarValores";
import PageContainer from "../../components/PageContainer";

const GraficoCaixaGeral = () => {
  const [loading, setLoading] = useState(false);
  const [dadosCaixa, setDadosCaixa] = useState(null);
  const [dadosGrafico, setDadosGrafico] = useState([]);
  const [resumoFormasPagamento, setResumoFormasPagamento] = useState([]);
  const [resumoTipoCaixa, setResumoTipoCaixa] = useState([]);
  const [filtros, setFiltros] = useState({
    dataInicial: getFirstDayOfMonth(),
    dataFinal: getLastDayOfMonth(),
  });

  function getFirstDayOfMonth() {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1)
      .toISOString()
      .split("T")[0];
  }

  function getLastDayOfMonth() {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0)
      .toISOString()
      .split("T")[0];
  }

  const processarDados = useCallback((dados) => {
    if (!dados || !dados.caixa) {
      setDadosGrafico([]);
      setResumoFormasPagamento([]);
      setResumoTipoCaixa([]);
      return;
    }

    // Processar dados para gráfico - agrupar por data
    const dadosPorData = {};
    dados.caixa.forEach((caixa) => {
      const data = formatarData(caixa.data);
      if (!dadosPorData[data]) {
        dadosPorData[data] = 0;
      }
      dadosPorData[data] += caixa.valorTotal || 0;
    });

    const dadosGraficoFormatados = Object.entries(dadosPorData)
      .map(([data, valor]) => ({
        data,
        valor,
        valorFormatado: formatCurrencyMoeda(valor),
      }))
      .sort((a, b) => {
        // Converter dd/MM para Date para ordenação correta
        const [dayA, monthA] = a.data.split("/");
        const [dayB, monthB] = b.data.split("/");
        const dateA = new Date(2024, monthA - 1, dayA);
        const dateB = new Date(2024, monthB - 1, dayB);
        return dateA - dateB;
      });

    setDadosGrafico(dadosGraficoFormatados);

    // Processar formas de pagamento
    const formasPagamento = {};
    dados.caixa.forEach((caixa) => {
      // Formas de pagamento existentes
      if (caixa.caixaMovimento) {
        caixa.caixaMovimento.forEach((movimento) => {
          const nome = movimento.formaPagamento?.nome || "Não informado";
          if (!formasPagamento[nome]) {
            formasPagamento[nome] = 0;
          }
          formasPagamento[nome] += movimento.valorMovimento || 0;
        });
      }

      // Somar valorSangria ao Dinheiro
      if (caixa.valorSangria && caixa.valorSangria > 0) {
        if (!formasPagamento["Dinheiro"]) {
          formasPagamento["Dinheiro"] = 0;
        }
        formasPagamento["Dinheiro"] += caixa.valorSangria - caixa.valorRetirada;
      }
    });

    const resumoFormas = Object.entries(formasPagamento).map(
      ([nome, valor]) => ({
        nome,
        valor,
        valorFormatado: formatCurrencyMoeda(valor),
      })
    );
    setResumoFormasPagamento(resumoFormas);

    // Processar Tipo de Caixa
    const tipoCaixa = {};
    dados.caixa.forEach((caixa) => {
      if (caixa.caixaMovimentoTipoCaixa) {
        caixa.caixaMovimentoTipoCaixa.forEach((movimento) => {
          const nome = movimento.tipoCaixa?.nome || "Não informado";
          if (!tipoCaixa[nome]) {
            tipoCaixa[nome] = 0;
          }
          tipoCaixa[nome] += movimento.valorMovimento || 0;
        });
      }
    });

    const resumoTp = Object.entries(tipoCaixa).map(([nome, valor]) => ({
      nome,
      valor,
      valorFormatado: formatCurrencyMoeda(valor),
    }));
    setResumoTipoCaixa(resumoTp);
  }, []);

  const loadDados = useCallback(async () => {
    setLoading(true);
    try {
      const dados = await caixaGeralService.listarCaixaGeralEntreDatas(
        filtros.dataInicial,
        filtros.dataFinal
      );
      setDadosCaixa(dados);
      processarDados(dados);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, [filtros, processarDados]);

  useEffect(() => {
    loadDados();
  }, [loadDados]);

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const aplicarFiltros = () => {
    loadDados();
  };

  const cores = [
    "#8884d8",
    "#82ca9d",
    "#ffc658",
    "#ff7300",
    "#00ff7f",
    "#ff6347",
    "#40e0d0",
    "#ee82ee",
  ];

  return (
    <PageContainer className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Relatório Caixa Geral
        </h1>
        <p className="text-gray-600">
          Análise detalhada dos caixas por período
        </p>
      </div>

      {/* Filtros */}
      <div className="mb-6 p-4 bg-white rounded-lg border shadow">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Data Inicial
            </label>
            <input
              type="date"
              name="dataInicial"
              value={filtros.dataInicial}
              onChange={handleFiltroChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Data Final
            </label>
            <input
              type="date"
              name="dataFinal"
              value={filtros.dataFinal}
              onChange={handleFiltroChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <button
              onClick={aplicarFiltros}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Calendar size={16} />
              {loading ? "Carregando..." : "Aplicar Filtros"}
            </button>
          </div>
        </div>
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Valor Total</p>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrencyMoeda(dadosCaixa?.valorTotal || 0)}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Total de Caixas
              </p>
              <p className="text-2xl font-bold text-blue-600">
                {dadosCaixa?.caixa?.length || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <Calculator className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Média Diária</p>
              <p className="text-2xl font-bold text-purple-600">
                {formatCurrencyMoeda(
                  dadosCaixa?.caixa?.length > 0
                    ? (dadosCaixa?.valorTotal || 0) / dadosCaixa.caixa.length
                    : 0
                )}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Gráfico de Valores por Data */}
      <div className="bg-white rounded-lg shadow border p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <TrendingUp size={20} />
          Evolução Diária dos Caixas
        </h3>

        {loading ? (
          <div className="h-80 flex items-center justify-center">
            <div className="text-gray-500">Carregando dados...</div>
          </div>
        ) : dadosGrafico.length === 0 ? (
          <div className="h-80 flex items-center justify-center">
            <div className="text-gray-500">
              Nenhum dado encontrado para o período selecionado
            </div>
          </div>
        ) : (
          <div style={{ width: "100%", height: "400px" }}>
            <ResponsiveContainer>
              <BarChart
                data={dadosGrafico}
                margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="data"
                  tick={{ fontSize: 12, fill: "#6b7280" }}
                  axisLine={{ stroke: "#d1d5db" }}
                  tickLine={{ stroke: "#d1d5db" }}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: "#6b7280" }}
                  axisLine={{ stroke: "#d1d5db" }}
                  tickLine={{ stroke: "#d1d5db" }}
                  tickFormatter={(value) =>
                    new Intl.NumberFormat("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    }).format(value)
                  }
                />
                <Tooltip
                  formatter={(value) => [
                    formatCurrencyMoeda(value),
                    "Valor do Caixa",
                  ]}
                  labelFormatter={(label) => `Data: ${label}`}
                  contentStyle={{
                    backgroundColor: "#f9fafb",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  }}
                  labelStyle={{ color: "#374151", fontWeight: "600" }}
                />
                <Bar
                  dataKey="valor"
                  fill="#6366f1"
                  stroke="#4f46e5"
                  strokeWidth={1}
                  radius={[2, 2, 0, 0]}
                  maxBarSize={50}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Resumos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Formas de Pagamento */}
        <div className="bg-white rounded-lg shadow border p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <CreditCard size={20} />
            Formas de Pagamento
          </h3>

          {resumoFormasPagamento.length === 0 ? (
            <div className="text-gray-500 text-center py-4">
              Nenhuma forma de pagamento encontrada
            </div>
          ) : (
            <>
              <div className="mb-4">
                <div style={{ width: "100%", height: "250px" }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={resumoFormasPagamento}
                        cx="50%"
                        cy="50%"
                        outerRadius={70}
                        fill="#8884d8"
                        dataKey="valor"
                        label={false}
                      >
                        {resumoFormasPagamento.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={cores[index % cores.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => formatCurrencyMoeda(value)}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="space-y-2 max-h-48 overflow-y-auto">
                {resumoFormasPagamento.map((forma, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-center p-3 bg-gray-50 rounded"
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: cores[index % cores.length] }}
                      ></div>
                      <span className="text-sm font-medium">{forma.nome}</span>
                    </div>
                    <span className="text-sm font-bold text-green-600">
                      {forma.valorFormatado}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Tipo de Caixa */}
        <div className="bg-white rounded-lg shadow border p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Layers2 size={20} />
            Caixas
          </h3>

          {resumoTipoCaixa.length === 0 ? (
            <div className="text-gray-500 text-center py-4">
              Nenhum tipo de caixa encontrado
            </div>
          ) : (
            <>
              <div className="mb-4">
                <div style={{ width: "100%", height: "250px" }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={resumoTipoCaixa}
                        cx="50%"
                        cy="50%"
                        outerRadius={70}
                        fill="#8884d8"
                        dataKey="valor"
                        label={false}
                      >
                        {resumoTipoCaixa.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={cores[index % cores.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => formatCurrencyMoeda(value)}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="space-y-2 max-h-48 overflow-y-auto">
                {resumoTipoCaixa.map((tipoCaixa, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-center p-3 bg-gray-50 rounded"
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: cores[index % cores.length] }}
                      ></div>
                      <span className="text-sm font-medium">
                        {tipoCaixa.nome}
                      </span>
                    </div>
                    <span className="text-sm font-bold text-blue-600">
                      {tipoCaixa.valorFormatado}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </PageContainer>
  );
};

export default GraficoCaixaGeral;
